import { useMemo } from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Select, MenuItem, Chip, ToggleButtonGroup, ToggleButton, Avatar } from '@mui/material';
import {
  PageHeader,
  SectionCard,
  LoadingState,
} from '@/components/shared';
import permissionsService, {
  AccessLevel,
  PermissionMatrix,
  UserPermissionRow,
  MODULE_LABELS,
  ACCESS_LABELS,
  MODULES,
} from '@/services/permissions';
import { formattedRoles } from '@/types';

type Props = {
  matrix: PermissionMatrix;
  users: UserPermissionRow[];
  isLoading: boolean;
  isUsersLoading: boolean;
  isSaving: boolean;
  activeTab: 'roles' | 'users';
  onTabChange: (tab: 'roles' | 'users') => void;
  onAccessChange: (roleId: string, module: string, access: AccessLevel) => void;
  onSetOwner: (profileId: string) => void;
  onRemoveOwner: (profileId: string) => void;
};

const ACCESS_COLORS: Record<AccessLevel, { bg: string; color: string }> = {
  admin: { bg: '#DBEAFE', color: '#1E40AF' },
  editor: { bg: '#F0FDF4', color: '#166534' },
  viewer: { bg: '#FEF3C7', color: '#92400E' },
  none: { bg: '#F4F4F5', color: '#71717A' },
};

const ACCESS_ICONS: Record<AccessLevel, typeof AdminPanelSettingsIcon> = {
  admin: AdminPanelSettingsIcon,
  editor: EditIcon,
  viewer: VisibilityIcon,
  none: BlockIcon,
};

function formatRoleName(roleValue: string): string {
  return (formattedRoles as Record<string, string>)[roleValue] ?? roleValue;
}

function getRoleCategory(roleValue: string): string {
  return roleValue.split('.')[0];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const CATEGORY_ORDER = ['PEOPLE', 'HIRING', 'ACADEMY', 'SEO', 'FINANCE'];

const tableStyles = {
  overflowX: 'auto' as const,
  WebkitOverflowScrolling: 'touch' as const,
  '& table': {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 13,
  },
  '& thead th': {
    textAlign: 'center' as const,
    padding: '10px 12px',
    color: '#71717A',
    fontWeight: 500,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: '2px solid #E4E4E7',
    whiteSpace: 'nowrap' as const,
  },
  '& thead th:first-of-type': { textAlign: 'left' as const },
  '& tbody td': {
    padding: '8px 12px',
    borderBottom: '1px solid #F4F4F5',
    verticalAlign: 'middle' as const,
    textAlign: 'center' as const,
  },
  '& tbody td:first-of-type': { textAlign: 'left' as const },
  '& tbody tr': {
    transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
    '&:hover': { backgroundColor: '#FAFAFA' },
    '&:active': { backgroundColor: '#F4F4F5' },
  },
};

function AccessChipDisplay({ access }: { access: AccessLevel }): JSX.Element {
  const colors = ACCESS_COLORS[access];
  const Icon = ACCESS_ICONS[access];
  return (
    <Chip
      icon={
        <Icon
          sx={{ fontSize: '14px !important', color: `${colors.color} !important` }}
        />
      }
      label={ACCESS_LABELS[access]}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        fontWeight: 500,
        fontSize: 12,
        height: 26,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

function AccessDropdown({
  value,
  onChange,
}: {
  value: AccessLevel;
  onChange: (val: AccessLevel) => void;
}): JSX.Element {
  const colors = ACCESS_COLORS[value];
  const Icon = ACCESS_ICONS[value];

  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as AccessLevel)}
      size="small"
      variant="outlined"
      renderValue={(val) => (
        <Chip
          icon={
            <Icon
              sx={{
                fontSize: '14px !important',
                color: `${colors.color} !important`,
              }}
            />
          }
          label={ACCESS_LABELS[val as AccessLevel]}
          size="small"
          sx={{
            backgroundColor: colors.bg,
            color: colors.color,
            fontWeight: 500,
            fontSize: 12,
            height: 26,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}
      sx={{
        minWidth: 120,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#F4F4F5' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E4E4E7' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#054AD9',
        },
        '& .MuiSelect-select': { py: 0.5, px: 1 },
      }}
    >
      {(['admin', 'editor', 'viewer', 'none'] as AccessLevel[]).map((level) => {
        const lc = ACCESS_COLORS[level];
        const LIcon = ACCESS_ICONS[level];
        return (
          <MenuItem key={level} value={level} sx={{ fontSize: 13, gap: 1, py: 1 }}>
            <LIcon sx={{ fontSize: 16, color: lc.color }} />
            <Box>
              <Box sx={{ fontWeight: 500 }}>{ACCESS_LABELS[level]}</Box>
              <Box sx={{ fontSize: 11, color: '#71717A' }}>
                {level === 'admin' &&
                  'Full access — create, edit, delete, settings'}
                {level === 'editor' && 'Standard — create and edit'}
                {level === 'viewer' &&
                  'Read-only — view dashboards and reports'}
                {level === 'none' && 'No access to this module'}
              </Box>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
}

const PermissionsManagementComponent = function ({
  matrix,
  users,
  isLoading,
  isUsersLoading,
  isSaving,
  activeTab,
  onTabChange,
  onAccessChange,
  onSetOwner,
  onRemoveOwner,
}: Props): JSX.Element {
  const groupedRoles = useMemo(() => {
    const groups: Record<string, Array<{ id: string; value: string }>> = {};
    for (const role of matrix.roles) {
      const cat = getRoleCategory(role.value);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(role);
    }
    return groups;
  }, [matrix.roles]);

  const sortedCategories = useMemo(
    () =>
      Object.keys(groupedRoles).sort(
        (a, b) =>
          (CATEGORY_ORDER.indexOf(a) === -1 ? 99 : CATEGORY_ORDER.indexOf(a)) -
          (CATEGORY_ORDER.indexOf(b) === -1 ? 99 : CATEGORY_ORDER.indexOf(b))
      ),
    [groupedRoles]
  );

  return (
    <>
      <PageHeader
        title="Permissions"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isSaving && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: 13,
                  color: '#054AD9',
                }}
              >
                <SaveIcon sx={{ fontSize: 16 }} />
                Saving...
              </Box>
            )}
            <ToggleButtonGroup
              value={activeTab}
              exclusive
              onChange={(_, val) => val && onTabChange(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 13,
                  px: 1.5,
                  py: 0.5,
                  borderColor: '#E4E4E7',
                  '&.Mui-selected': {
                    backgroundColor: '#054AD915',
                    color: '#054AD9',
                    borderColor: '#054AD9',
                    '&:hover': { backgroundColor: '#054AD925' },
                  },
                },
              }}
            >
              <ToggleButton value="roles">Roles</ToggleButton>
              <ToggleButton value="users">Users</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
      />

      {/* ── Roles Tab ── */}
      {activeTab === 'roles' && (
        <SectionCard
          title="Role Permissions Matrix"
          subtitle="Set the access level for each role per module. Changes are saved automatically."
        >
          {isLoading ? (
            <LoadingState message="Loading permissions..." />
          ) : (
            <Box sx={{ ...tableStyles, '& table': { ...tableStyles['& table'], minWidth: matrix.modules.length * 140 + 200 } }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 180 }}>Role</th>
                    {matrix.modules.map((mod) => (
                      <th key={mod}>{MODULE_LABELS[mod] ?? mod}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedCategories.map((category) => [
                    <tr key={`cat-${category}`}>
                      <td
                        colSpan={matrix.modules.length + 1}
                        style={{
                          padding: '12px 12px 6px',
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#09090B',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderBottom: '1px solid #E4E4E7',
                          backgroundColor: '#FAFAFA',
                        }}
                      >
                        {category}
                      </td>
                    </tr>,
                    ...groupedRoles[category].map((role) => (
                      <tr key={role.id}>
                        <td>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                fontWeight: 500,
                                fontSize: 13,
                                color: '#09090B',
                              }}
                            >
                              {formatRoleName(role.value)}
                            </Box>
                            <Box sx={{ fontSize: 11, color: '#71717A' }}>
                              {role.value}
                            </Box>
                          </Box>
                        </td>
                        {matrix.modules.map((mod) => {
                          const currentAccess =
                            permissionsService.getAccessForRole(
                              matrix.entries,
                              role.id,
                              mod
                            );
                          return (
                            <td key={mod}>
                              <AccessDropdown
                                value={currentAccess}
                                onChange={(val) =>
                                  onAccessChange(role.id, mod, val)
                                }
                              />
                            </td>
                          );
                        })}
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </Box>
          )}
        </SectionCard>
      )}

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (
        <SectionCard
          title="User Access Overview"
          subtitle="View each user's effective permissions based on their assigned roles. Star a user to make them Account Owner (full access to everything)."
        >
          {isUsersLoading ? (
            <LoadingState message="Loading users..." />
          ) : (
            <Box sx={{ ...tableStyles, '& table': { ...tableStyles['& table'], minWidth: MODULES.length * 120 + 300 } }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 240 }}>User</th>
                    <th style={{ minWidth: 120, textAlign: 'left' }}>Roles</th>
                    {MODULES.map((mod) => (
                      <th key={mod}>{MODULE_LABELS[mod]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.profileId}
                      style={
                        user.isOwner
                          ? { backgroundColor: '#FFFBEB' }
                          : undefined
                      }
                    >
                      <td>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Avatar
                              src={user.pictureUrl ?? undefined}
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: user.isOwner
                                  ? '#F59E0B'
                                  : '#054AD9',
                              }}
                            >
                              {getInitials(user.name)}
                            </Avatar>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  fontWeight: 500,
                                  fontSize: 13,
                                  color: '#09090B',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {user.name}
                              </Box>
                              {user.isOwner && (
                                <Chip
                                  icon={
                                    <StarIcon
                                      sx={{
                                        fontSize: '12px !important',
                                        color: '#D97706 !important',
                                      }}
                                    />
                                  }
                                  label="Owner"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    backgroundColor: '#FEF3C7',
                                    color: '#92400E',
                                    fontWeight: 500,
                                    fontSize: 11,
                                    '& .MuiChip-label': { px: 0.5 },
                                  }}
                                />
                              )}
                            </Box>
                            <Box
                              sx={{
                                fontSize: 12,
                                color: '#71717A',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {user.email}
                            </Box>
                          </Box>
                          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                            {user.isOwner ? (
                              <button
                                type="button"
                                onClick={() => onRemoveOwner(user.profileId)}
                                title="Remove as Account Owner"
                                style={{
                                  all: 'unset',
                                  cursor: 'pointer',
                                  display: 'flex',
                                }}
                              >
                                <StarIcon
                                  sx={{
                                    fontSize: 18,
                                    color: '#F59E0B',
                                    '&:hover': { color: '#D97706' },
                                  }}
                                />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onSetOwner(user.profileId)}
                                title="Set as Account Owner"
                                style={{
                                  all: 'unset',
                                  cursor: 'pointer',
                                  display: 'flex',
                                }}
                              >
                                <StarBorderIcon
                                  sx={{
                                    fontSize: 18,
                                    color: '#D4D4D8',
                                    '&:hover': { color: '#F59E0B' },
                                  }}
                                />
                              </button>
                            )}
                          </Box>
                        </Box>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          {user.roles.length === 0 && (
                            <Box sx={{ fontSize: 12, color: '#71717A' }}>
                              No roles
                            </Box>
                          )}
                          {user.roles.map((r) => (
                            <Chip
                              key={r}
                              label={formatRoleName(r)}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 500,
                                backgroundColor: '#F4F4F5',
                                color: '#4A4B4E',
                              }}
                            />
                          ))}
                        </Box>
                      </td>
                      {MODULES.map((mod) => (
                        <td key={mod}>
                          <AccessChipDisplay
                            access={user.effectiveAccess[mod] ?? 'none'}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </SectionCard>
      )}

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {(['admin', 'editor', 'viewer', 'none'] as AccessLevel[]).map(
          (level) => {
            const colors = ACCESS_COLORS[level];
            const Icon = ACCESS_ICONS[level];
            return (
              <Box
                key={level}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: 12,
                  color: '#4A4B4E',
                }}
              >
                <Icon sx={{ fontSize: 14, color: colors.color }} />
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {ACCESS_LABELS[level]}
                </Box>
                <Box component="span" sx={{ color: '#71717A' }}>
                  {level === 'admin' && '— create, edit, delete, settings'}
                  {level === 'editor' && '— create, edit'}
                  {level === 'viewer' && '— read-only'}
                  {level === 'none' && '— hidden'}
                </Box>
              </Box>
            );
          }
        )}
      </Box>
    </>
  );
};

export default PermissionsManagementComponent;
